<template>
  <div class="reader-page">
    <!-- 顶部导航栏 -->
    <div class="reader-header">
      <div class="left-actions">
        <button class="back-btn" @click="goBack">
          <i class="icon-back"></i>
          返回
        </button>
        <h1 class="comic-title">{{ comicTitle }}</h1>
      </div>
      <div class="chapter-info">
        <span class="chapter-title">{{ chapterTitle }}</span>
      </div>
      <div class="right-actions">
        <button class="toggle-chapters-btn" @click="toggleChapterList">
          目录
        </button>
      </div>
    </div>

    <!-- 章节内容 -->
    <div class="reader-content" v-if="loading">
      <div class="loading">
        <div class="spinner"></div>
        <p>正在加载...</p>
      </div>
    </div>
    <div class="reader-content" v-else-if="error">
      <div class="error-message">
        <p>{{ error }}</p>
        <button @click="fetchChapterDetail" class="retry-btn">重试</button>
      </div>
    </div>
    <div class="reader-content" v-else>
      <!-- 免费章节或已购买章节内容 -->
      <div v-if="canAccess" class="chapter-content">
        <div v-for="(image, index) in contentImages" :key="index" class="image-container">
          <img 
            :src="getFullImageUrl(image)" 
            :alt="`${chapterTitle} - 图片${index + 1}`"
            @error="handleImageError"
            @load="imageLoaded"
          />
        </div>
      </div>
      
      <!-- 付费章节提示 -->
      <div v-else class="paywall">
        <div class="paywall-info">
          <div class="lock-icon">
            <i class="icon-lock"></i>
          </div>
          <h3>该章节需要购买后才能阅读</h3>
          <p class="reason">{{ accessInfo.reason }}</p>
          
          <div class="price-info">
            <span class="price">{{ accessInfo.price }} 金币</span>
            <span class="user-coins">当前余额: {{ userCoins }} 金币</span>
          </div>
          
          <div v-if="purchaseError" class="purchase-error">
            <i class="error-icon"></i> {{ purchaseError }}
          </div>
          
          <template v-if="userCoins < accessInfo.price">
            <div class="recharge-tip">
              <i class="tip-icon"></i>
              <span>金币不足，请先充值</span>
            </div>
            <button class="recharge-btn" @click="goToRechargePage">
              <i class="coin-icon"></i> 去充值
            </button>
          </template>
          <button 
            v-else
            class="purchase-btn" 
            @click="handlePurchase" 
            :disabled="purchaseLoading"
          >
            <span v-if="purchaseLoading">
              <div class="small-spinner"></div>
              购买中...
            </span>
            <span v-else>购买本章</span>
          </button>
          
          <div class="vip-info" v-if="!userInfo.isVip">
            <p>开通VIP会员可享受漫画章节优惠</p>
            <button class="vip-btn" @click="goToVipPage">
              <i class="vip-icon"></i> 开通VIP
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部导航 -->
    <div class="reader-footer">
      <button 
        class="nav-btn prev" 
        :disabled="!prevChapter" 
        @click="navigateToChapter(prevChapter?.id)"
      >
        上一章
      </button>
      <button 
        class="nav-btn next" 
        :disabled="!nextChapter" 
        @click="navigateToChapter(nextChapter?.id)"
      >
        下一章
      </button>
    </div>
    
    <!-- 章节列表侧边栏 -->
    <div class="chapter-list-sidebar" :class="{ active: showChapterList }">
      <div class="chapter-list-header">
        <h2>章节列表</h2>
        <button class="close-btn" @click="toggleChapterList">
          <i class="icon-close"></i>
        </button>
      </div>
      <div v-if="loadingChapters" class="chapter-list-loading">
        <div class="spinner"></div>
      </div>
      <div v-else-if="chaptersError" class="chapter-list-error">
        <p>{{ chaptersError }}</p>
        <button @click="fetchChapterList" class="retry-btn">重试</button>
      </div>
      <div v-else class="chapter-list">
        <div 
          v-for="chapter in chapters" 
          :key="chapter.id" 
          :class="['chapter-item', { 
            active: chapter.id === chapterId,
            free: chapter.is_free === 1,
            locked: !chapterAccessMap[chapter.id]?.canAccess
          }]"
          @click="navigateToChapter(chapter.id)"
        >
          <span class="chapter-name">{{ chapter.title }}</span>
          <span v-if="chapter.is_free === 1" class="free-tag">免费</span>
          <span v-else-if="!chapterAccessMap[chapter.id]?.canAccess" class="lock-tag">
            <i class="icon-lock-small"></i>
          </span>
        </div>
      </div>
    </div>
    
    <!-- 遮罩层 -->
    <div 
      v-if="showChapterList" 
      class="mask" 
      @click="toggleChapterList"
    ></div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getChapterDetail, getChapterList, buyChapter } from '@/api/chapter';
import { getApiUrl } from '@/api/config';
import { getUserInfo, isLoggedIn } from '@/api/user';

export default {
  name: 'ReaderPage',
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    // 参数和状态
    const chapterId = ref(null);
    const chapterDetail = ref(null);
    const loading = ref(true);
    const error = ref('');
    const loadedImagesCount = ref(0);
    
    // 章节列表相关
    const showChapterList = ref(false);
    const chapters = ref([]);
    const loadingChapters = ref(false);
    const chaptersError = ref('');
    const chapterAccessMap = ref({});
    
    // 用户相关
    const userCoins = ref(0);
    const purchaseLoading = ref(false);
    const purchaseError = ref('');
    
    // 计算属性
    const comicTitle = computed(() => chapterDetail.value?.comic?.title || '');
    const chapterTitle = computed(() => chapterDetail.value?.chapter?.title || '');
    const contentImages = computed(() => {
      if (!chapterDetail.value || !chapterDetail.value.content) return [];
      return chapterDetail.value.content.content || [];
    });
    const canAccess = computed(() => {
      if (!chapterDetail.value || !chapterDetail.value.accessInfo) return false;
      return chapterDetail.value.accessInfo.canAccess;
    });
    const accessInfo = computed(() => {
      if (!chapterDetail.value || !chapterDetail.value.accessInfo) return {};
      return chapterDetail.value.accessInfo;
    });
    const userInfo = computed(() => {
      if (!chapterDetail.value || !chapterDetail.value.userInfo) return { isVip: false };
      return chapterDetail.value.userInfo;
    });
    const prevChapter = computed(() => chapterDetail.value?.prevChapter || null);
    const nextChapter = computed(() => chapterDetail.value?.nextChapter || null);
    
    // 方法
    const fetchChapterDetail = async () => {
      if (!chapterId.value) return;
      
      loading.value = true;
      error.value = '';
      loadedImagesCount.value = 0;
      
      try {
        // 获取最新用户信息（包括金币）
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const userInfoData = await getUserInfo(token);
            userCoins.value = userInfoData.coins || 0;
            console.log('加载章节时获取的用户金币:', userCoins.value);
          }
        } catch (userErr) {
          console.error('获取用户金币失败:', userErr);
        }
        
        const response = await getChapterDetail(chapterId.value);
        
        if (response.code === 1 && response.data) {
          chapterDetail.value = response.data;
          
          // 如果没有获取过章节列表，获取章节列表
          if (!chapters.value.length && chapterDetail.value.comic) {
            fetchChapterList(chapterDetail.value.comic.id);
          }
        } else {
          error.value = response.msg || '获取章节数据失败';
        }
      } catch (err) {
        error.value = '网络错误，请稍后重试';
        console.error('获取章节详情失败:', err);
      } finally {
        loading.value = false;
      }
    };
    
    const fetchChapterList = async (comicId) => {
      if (!comicId) return;
      
      loadingChapters.value = true;
      chaptersError.value = '';
      
      try {
        const response = await getChapterList(comicId);
        
        if (response.code === 1 && response.data) {
          chapters.value = response.data.list || [];
          
          // 保存章节访问权限信息
          if (response.data.chapterAccess) {
            chapterAccessMap.value = response.data.chapterAccess;
          }
        } else {
          chaptersError.value = response.msg || '获取章节列表失败';
        }
      } catch (err) {
        chaptersError.value = '网络错误，请稍后重试';
        console.error('获取章节列表失败:', err);
      } finally {
        loadingChapters.value = false;
      }
    };
    
    const toggleChapterList = () => {
      showChapterList.value = !showChapterList.value;
    };
    
    const navigateToChapter = (id) => {
      if (!id) return;
      router.push({ name: 'Reader', params: { id } });
      toggleChapterList();
    };
    
    const goBack = () => {
      if (chapterDetail.value && chapterDetail.value.comic) {
        router.push(`/comic/${chapterDetail.value.comic.id}`);
      } else {
        router.back();
      }
    };
    
    const handlePurchase = async () => {
      // 检查登录状态
      if (!isLoggedIn()) {
        error.value = '请先登录';
        return;
      }

      purchaseLoading.value = true;
      purchaseError.value = '';

      try {
        // 获取最新用户信息（包括金币）
        const token = localStorage.getItem('token');
        const userInfoData = await getUserInfo(token);
        userCoins.value = userInfoData.coins || 0;
        
        // 打印用户金币信息
        console.log('用户金币信息:', {
          coins: userInfoData.coins,
          type: typeof userInfoData.coins,
          price: accessInfo.value.price,
          priceType: typeof accessInfo.value.price
        });

        // 检查金币是否足够
        if (userCoins.value < accessInfo.value.price) {
          purchaseError.value = '金币不足，请先充值';
          return;
        }

        // 发起购买请求
        const response = await buyChapter(chapterId.value);
        console.log('购买章节响应:', response);
        
        if (response.code === 1) {
          // 购买成功，重新加载章节
          fetchChapterDetail();
        } else {
          purchaseError.value = response.msg || '购买失败';
        }
      } catch (err) {
        purchaseError.value = err.message || '网络错误，请稍后重试';
        console.error('购买章节失败:', err);
      } finally {
        purchaseLoading.value = false;
      }
    };
    
    const goToVipPage = () => {
      // TODO: 跳转到VIP页面
      router.push('/vip');
    };
    
    const goToRechargePage = () => {
      router.push('/user/recharge');
    };
    
    const getFullImageUrl = (path) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return getApiUrl(path);
    };
    
    const handleImageError = (e) => {
      e.target.src = '';
      e.target.classList.add('error');
    };
    
    const imageLoaded = () => {
      loadedImagesCount.value++;
    };
    
    // 监听路由参数变化
    watch(() => route.params.id, (newId) => {
      if (newId) {
        chapterId.value = parseInt(newId);
        fetchChapterDetail();
      }
    }, { immediate: true });
    
    return {
      chapterId,
      chapterDetail,
      loading,
      error,
      loadedImagesCount,
      showChapterList,
      chapters,
      loadingChapters,
      chaptersError,
      chapterAccessMap,
      comicTitle,
      chapterTitle,
      contentImages,
      canAccess,
      accessInfo,
      userInfo,
      prevChapter,
      nextChapter,
      userCoins,
      purchaseLoading,
      purchaseError,
      fetchChapterDetail,
      fetchChapterList,
      toggleChapterList,
      navigateToChapter,
      goBack,
      handlePurchase,
      goToVipPage,
      goToRechargePage,
      getFullImageUrl,
      handleImageError,
      imageLoaded
    };
  }
};
</script>

<style scoped>
.reader-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f5f5;
  position: relative;
}

/* 顶部导航栏 */
.reader-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background-color: #1f2937;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.left-actions, .right-actions {
  display: flex;
  align-items: center;
}

.chapter-info {
  text-align: center;
  flex: 1;
}

.comic-title {
  font-size: 16px;
  margin: 0 10px;
}

.chapter-title {
  font-size: 14px;
  opacity: 0.8;
}

.back-btn, .toggle-chapters-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 5px 10px;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.icon-back::before {
  content: "←";
  margin-right: 5px;
}

/* 章节内容 */
.reader-content {
  flex: 1;
  padding: 10px 0;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.chapter-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-container {
  width: 100%;
  margin-bottom: 0; /* 移除图片之间的间距 */
}

.image-container img {
  width: 100%;
  display: block;
  background-color: #fff;
}

.image-container img.error {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  color: #999;
  font-size: 14px;
}

.image-container img.error::after {
  content: "图片加载失败";
}

/* 付费墙 */
.paywall {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 30px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin: 30px auto;
  max-width: 450px;
  position: relative;
  overflow: hidden;
}

.paywall::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #ff6b81, #ff8e9e);
}

.paywall-info {
  text-align: center;
  width: 100%;
}

.lock-icon {
  font-size: 48px;
  color: #ff6b81;
  margin-bottom: 25px;
  background-color: rgba(255, 107, 129, 0.1);
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto 25px;
}

.icon-lock::before {
  content: "🔒";
}

.paywall h3 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}

.reason {
  color: #666;
  margin-bottom: 20px;
  font-size: 15px;
}

.price-info {
  margin: 25px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background-color: #f9f9f9;
  padding: 15px 20px;
  border-radius: 8px;
  width: 100%;
}

.price {
  font-size: 28px;
  font-weight: bold;
  color: #ff6b81;
  display: flex;
  align-items: center;
}

.price::before {
  content: '￥';
  font-size: 20px;
  margin-right: 2px;
}

.user-coins {
  font-size: 14px;
  color: #666;
  background-color: rgba(0, 0, 0, 0.03);
  padding: 6px 12px;
  border-radius: 20px;
}

.purchase-error {
  color: #e74c3c;
  margin-bottom: 20px;
  padding: 10px 15px;
  background-color: rgba(231, 76, 60, 0.08);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  text-align: center;
}

.purchase-btn {
  background: linear-gradient(90deg, #ff6b81, #ff8e9e);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 5px;
  box-shadow: 0 4px 10px rgba(255, 107, 129, 0.2);
}

.purchase-btn:hover:not(:disabled) {
  background: linear-gradient(90deg, #ff5a72, #ff7d8f);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(255, 107, 129, 0.3);
}

.purchase-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(255, 107, 129, 0.2);
}

.purchase-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: linear-gradient(90deg, #ff6b81, #ff8e9e);
}

.small-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

.vip-info {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  width: 100%;
}

.vip-info p {
  color: #666;
  font-size: 14px;
  margin-bottom: 12px;
}

.vip-btn {
  background: linear-gradient(90deg, #ffd700, #ffcc00);
  color: #333;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-block;
  box-shadow: 0 4px 10px rgba(255, 215, 0, 0.2);
}

.vip-btn:hover {
  background: linear-gradient(90deg, #ffc800, #ffbb00);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(255, 215, 0, 0.3);
}

/* 底部导航 */
.reader-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 20px;
  background-color: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.nav-btn {
  background-color: #f0f0f0;
  border: none;
  padding: 10px 20px;
  margin: 0 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.nav-btn:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 章节列表侧边栏 */
.chapter-list-sidebar {
  position: fixed;
  top: 0;
  right: -300px;
  width: 300px;
  height: 100%;
  background-color: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  transition: right 0.3s;
  display: flex;
  flex-direction: column;
}

.chapter-list-sidebar.active {
  right: 0;
}

.chapter-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.chapter-list-header h2 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
}

.icon-close::before {
  content: "×";
}

.chapter-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
}

.chapter-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.3s;
}

.chapter-item:hover {
  background-color: #f5f5f5;
}

.chapter-item.active {
  background-color: #f0f0f0;
  font-weight: bold;
}

.chapter-name {
  flex: 1;
}

.free-tag {
  font-size: 12px;
  color: #4caf50;
  padding: 2px 6px;
  border-radius: 2px;
  background-color: rgba(76, 175, 80, 0.1);
}

.lock-tag {
  color: #ff6b81;
  font-size: 14px;
}

.icon-lock-small::before {
  content: "🔒";
  font-size: 12px;
}

.chapter-list-loading, .chapter-list-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  flex: 1;
}

/* 遮罩层 */
.mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
}

/* 加载状态 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  flex: 1;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 107, 129, 0.3);
  border-radius: 50%;
  border-top-color: #ff6b81;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  padding: 30px;
  color: #e74c3c;
}

.retry-btn {
  background-color: #ff6b81;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.recharge-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #ff6b81;
  margin-bottom: 15px;
  font-size: 15px;
  width: 100%;
  background-color: rgba(255, 107, 129, 0.08);
  padding: 10px;
  border-radius: 6px;
}

.tip-icon::before {
  content: "⚠️";
  font-size: 16px;
}

.error-icon::before {
  content: "❌";
  font-size: 14px;
  margin-right: 6px;
}

.recharge-btn {
  background: linear-gradient(90deg, #4dabf7, #339af0);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 15px;
  box-shadow: 0 4px 10px rgba(77, 171, 247, 0.2);
}

.recharge-btn:hover {
  background: linear-gradient(90deg, #339af0, #228be6);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(77, 171, 247, 0.3);
}

.coin-icon::before {
  content: "💰";
  font-size: 16px;
  margin-right: 8px;
}

.vip-icon::before {
  content: "👑";
  font-size: 16px;
  margin-right: 8px;
}
</style> 